import React, { useEffect, useState } from 'react'
import OffConvas from '../../components/OffConvas';
import OurClientForm from './components/OurClientForm';
import { clientService } from './services/clientService';
import { toast } from '../../services/toastService';
import Loading from '../../components/Loading';
import Pagination from '../../components/Pagination';
import DeleteConfirmModal from '../../components/DeleteConfirmModal';

const OurClients = () => {

  const [data, setData] = useState([]);
  const [id, setId] = useState(0);
  const [deleteId, setDeleteId] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    getAsync();
  }, [])

  const getAsync = async (pageNumber = 1) => {
    try {
      setLoading(true);
      const res = await clientService.getAll({
        pageNumber: pageNumber,
        pageSize: 15,
        orderBy: "displayOrder",
        orderDirection: "asc",
      });
      setData(res.value);
    } catch (error) {
      console.log(error);
    }
    finally {
      setLoading(false);
    }
  };

  const deleteAsync = async () => {
    try {
      setLoading(true);
      if (!deleteId) {
        toast.error("Please select a record to delete");
        return;
      }
      var result = await clientService.delete(deleteId);
      if (!result.isSuccess) {
        toast.error(result.errors[0] || "Failed to delete record");
        return;
      }
      toast.success("Record deleted successfully");
      setDeleteId(0);
      if (!search)
        getAsync(data.pageNumber);
      else
        getAllSearchAsync(data.pageNumber);
    } catch (error) {
      toast.error("An error occurred while deleting the record");
    }
    finally {
      setLoading(false);
    }
  }

  const getAllSearchAsync = async (pageNumber = 1, signal) => {
    try {
      var res = await clientService.getAllSearchAsync({
        search: search,
        pageNumber: pageNumber,
        pageSize: 15,
        orderBy: "displayOrder",
        orderDirection: "asc",
      }, signal);
      setData(res.value);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (search.trim().length < 2) {
      getAsync(1);
      return;
    }

    const controller = new AbortController();
    const delay = setTimeout(() => {
      getAllSearchAsync(1, controller.signal);
    }, 400);

    return () => {
      controller.abort();
      clearTimeout(delay);
    };
  }, [search]);

  const onPageChange = async (pageNumber) => {
    setLoading(true);
    if (!search) {
      getAsync(pageNumber);
      return;
    }
    await getAllSearchAsync(pageNumber);
  };

  const togglePublishAsync = async (item) => {
    try {
      setLoading(true);
      const result = await clientService.changePublishStatus(item.id, !item.isPublished);
      if (!result.isSuccess) {
        toast.error(result.errors[0] || "Failed to update publish status");
        return;
      }
      toast.success("Publish status updated successfully");
      if (!search) getAsync(data.pageNumber);
      else getAllSearchAsync(data.pageNumber);
    } catch (error) {
      toast.error("An error occurred while updating the publish status");
    } finally {
      setLoading(false);
    }
  };

  const moveAsync = async (item, direction) => {
    const items = data?.items || [];
    const index = items.findIndex((i) => i.id === item.id);
    const swapIndex = index + direction;
    if (index === -1 || swapIndex < 0 || swapIndex >= items.length) return;

    const swapWith = items[swapIndex];

    try {
      setLoading(true);
      const result = await clientService.reorder([
        { id: item.id, displayOrder: swapWith.displayOrder },
        { id: swapWith.id, displayOrder: item.displayOrder },
      ]);
      if (!result.isSuccess) {
        toast.error(result.errors[0] || "Failed to reorder");
        return;
      }
      if (!search) getAsync(data.pageNumber);
      else getAllSearchAsync(data.pageNumber);
    } catch (error) {
      toast.error("An error occurred while reordering");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <Loading />}
      <section className="section-documents">
        <div className="my-card">
          <div className="my-card-header">
            <div className="my-card-title">Our Clients</div>
            <button
              type="button"
              className="my-button my-button-dark"
              data-bs-toggle="offcanvas"
              data-bs-target="#my-client"
              onClick={() => setId(0)}
            >
              Add New
            </button>
          </div>
          <div className="my-card-body">
            <div className="my-grid">
              <div className="my-grid-header">
                <input
                  type="text"
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="form-control my-grid-search"
                />
              </div>
              <div className="my-grid-body">
                <table className="table table-bordered table-responsive">
                  <thead>
                    <tr>
                      <th>Logo</th>
                      <th>Name</th>
                      <th>Website</th>
                      <th>Order</th>
                      <th>Published</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.items?.length === 0 && (
                      <tr>
                        <td colSpan={6} className="text-center">No clients found.</td>
                      </tr>
                    )}
                    {data?.items?.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <img
                            src={item?.logoPath}
                            alt={item?.name}
                            style={{ width: 60, height: 45, objectFit: "contain", background: "#f5f5f5" }}
                          />
                        </td>
                        <td>{item?.name}</td>
                        <td>
                          {item?.websiteUrl && (
                            <a href={item.websiteUrl} target="_blank" rel="noopener noreferrer">
                              <i className="fas fa-external-link-alt" />
                            </a>
                          )}
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <span>{item?.displayOrder}</span>
                            <i className="fas fa-arrow-up cursor-pointer text-primary" onClick={() => moveAsync(item, -1)} />
                            <i className="fas fa-arrow-down cursor-pointer text-primary" onClick={() => moveAsync(item, 1)} />
                          </div>
                        </td>
                        <td>
                          <div className="form-check form-switch">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              role="switch"
                              checked={!!item?.isPublished}
                              onChange={() => togglePublishAsync(item)}
                            />
                          </div>
                        </td>
                        <td className="my-grid-actions">
                          <i
                            className="fas fa-pencil text-primary cursor-pointer"
                            data-bs-toggle="offcanvas"
                            data-bs-target="#my-client"
                            onClick={() => setId(item.id)}
                          />
                          <i
                            className="fas fa-trash text-danger cursor-pointer"
                            data-bs-toggle="modal"
                            data-bs-target="#confirm-modal"
                            onClick={() => setDeleteId(item.id)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="my-grid-footer">
                <Pagination
                  currentPage={data.pageNumber}
                  totalPages={data.totalPages}
                  onPageChange={onPageChange}
                />
              </div>
            </div>
          </div>
        </div>
      </section>
      <DeleteConfirmModal deleteAsync={deleteAsync} setDeleteId={setDeleteId} />
      <OffConvas
        title="Client"
        id="my-client"
      >
        <OurClientForm getAsync={getAsync} id={id} setId={setId} />
      </OffConvas>
    </>
  );
}

export default OurClients
